import { motion } from "framer-motion";
import { MessageCircle, ArrowDown, Star } from "lucide-react";
import { school, whatsappLink, heroVideo, heroPoster } from "@/data/content";

const lines = ["Um lugar seguro", "para crescer,", "brincar e aprender."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section id="topo" className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster={heroPoster}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-ink/30" />
      </div>

      {/* floating color blobs */}
      <div className="pointer-events-none absolute top-28 right-10 z-10 h-24 w-24 rounded-full bg-brand-yellow/80 blur-[2px] animate-float hidden md:block" />
      <div className="pointer-events-none absolute top-1/2 left-8 z-10 h-16 w-16 rounded-full bg-brand-pink/80 animate-float hidden md:block" style={{ animationDelay: "1.5s" }} />

      <div className="relative z-20 mx-auto max-w-7xl w-full px-5 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-4 py-2 mb-6"
        >
          <span className="flex items-center gap-1 text-brand-yellow">
            <Star className="h-4 w-4 fill-brand-yellow" strokeWidth={0} />
            <span className="font-700 text-white text-sm">{school.googleRating}</span>
          </span>
          <span className="text-white/85 text-sm font-600">no Google · Desde {school.founded}</span>
        </motion.div>

        <h1 className="font-display font-700 text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-6 max-w-xl text-white/90 text-lg font-500 leading-relaxed"
        >
          No Instituto Educacional Recriar, cada criança é acolhida com carinho,
          respeitada em sua individualidade e cuidada como parte da família.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#matriculas"
            data-testid="hero-enroll-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-700 text-white shadow-[0_10px_30px_rgb(255,159,28,0.45)] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(255,159,28,0.55)] transition-transform duration-300"
          >
            Matrículas abertas
          </a>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30 px-8 py-4 font-700 text-white hover:bg-white/25 transition-colors duration-300"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.5} /> Agende uma visita
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#sobre"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/80"
        aria-label="Rolar"
      >
        <ArrowDown className="h-6 w-6 animate-bounce" />
      </motion.a>
    </section>
  );
}
